// Setup › Model policy (`/c/{slug}/setup/model-policy`; MODEL-POLICY, owners and admins — the
// Setup layout gates managers): which gateway models the company's agents may use, the residency
// they must be served in, the company default and the default per task kind (`GET`/`PUT
// …/model-policy`, saved from the dock); whether people may bring their own AI keys (BYO-01's
// company flags, saved at once through `PATCH /v1/companies/{co}` like Keys & connections); and
// the per-agent model overrides, read-only, each linking to where it is changed.
import {
  Badge,
  Checkbox,
  EmptyState,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  Notice,
  PageHeader,
  SaveDock,
  SaveIndicator,
  SettingsAside,
  SettingsLayout,
  SettingsSection,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  ToggleRow,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { BotIcon, BoxesIcon, KeyRoundIcon, ListTreeIcon } from "lucide-react";
import { useEffect, useId, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { companyQuery, keys } from "../queries";
import { LoadError, Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { FLAG_OF } from "../subscriptions/company-setup";
import { ANTHROPIC_TERMS_URL, COPY } from "../subscriptions/copy";
import { subscriptionKeys } from "../subscriptions/queries";
import { EffortSection } from "./effort";
import {
  bodyOf,
  type Draft,
  draftOf,
  type ModelPolicyView,
  modelPolicyKey,
  modelPolicyQuery,
  type PolicyOverride,
  routableOf,
  sameDraft,
} from "./queries";

const KIND_LABEL: Record<string, string> = {
  lead: "Lead",
  draft: "Draft",
  review: "Review",
  verify: "Verify",
};
const kindLabel = (k: string) => KIND_LABEL[k] ?? k.replace(/_/g, " ");

export function ModelPolicyScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const q = useQuery(modelPolicyQuery(api, company.id));
  return (
    <>
      <PageHeader
        eyebrow="Setup"
        title="Model policy"
        subtitle="Which models your agents may use, the default for each kind of task, and whether people may bring their own AI keys."
      />
      {q.isPending ? (
        <Loading label="Loading the model policy" rows={6} />
      ) : q.isError ? (
        <LoadError what="The model policy" error={q.error} />
      ) : (
        <Body api={api} company={company} view={q.data} />
      )}
    </>
  );
}

function Body({ api, company, view }: { api: Api; company: CompanyAccess; view: ModelPolicyView }) {
  const qc = useQueryClient();
  const saved = useMemo(() => draftOf(view.policy), [view.policy]);
  const [draft, setDraft] = useState<Draft>(saved);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  // A refetch (another tab, a change ping) replaces a clean form; a dirty one is kept.
  const [base, setBase] = useState(saved);
  useEffect(() => {
    if (!sameDraft(saved, base)) {
      setDraft((d) => (sameDraft(d, base) ? saved : d));
      setBase(saved);
    }
  }, [saved, base]);
  const dirty = !sameDraft(draft, saved);
  const save = useMutation({
    mutationFn: () =>
      api.client.modelPolicy.put({ params: { co: company.id }, body: bodyOf(draft) }),
    onSuccess: (v) => {
      qc.setQueryData(modelPolicyKey(company.id), v);
      void qc.invalidateQueries({ queryKey: keys.company(company.id), exact: true });
      const next = draftOf(v.policy);
      setBase(next);
      setDraft(next);
      setSavedAt(Date.now());
    },
  });
  const set = (patch: Partial<Draft>) => {
    save.reset();
    setDraft((d) => ({ ...d, ...patch }));
  };
  const routable = routableOf(view, draft);

  return (
    <SettingsLayout
      aside={
        <SettingsAside title="How a run gets its model">
          <ol className="list-decimal space-y-1 pl-4">
            <li>The model the run itself names (refused if the policy does not allow it).</li>
            <li>An effort tier someone asked for (or vision, for images).</li>
            <li>The agent's own override, listed below.</li>
            <li>The run's effort tier model.</li>
            <li>The default for the task's kind.</li>
            <li>The company default.</li>
            <li>The first allowed model on the gateway.</li>
          </ol>
          <p className="mt-3">
            A default the policy excludes is skipped, never used. Every model is reached through the
            company's gateway with a per-run key; CompanyOS never routes requests through a person's
            Pro or Max plan.
          </p>
          <SaveIndicator savedAt={savedAt} className="mt-3" />
        </SettingsAside>
      }
    >
      {save.isError ? (
        <Notice tone="danger" data-slot="model-policy-error">
          Not saved: {problemText(save.error, "the policy was refused.")}
        </Notice>
      ) : null}
      <AllowedSection view={view} draft={draft} set={set} routable={routable} />
      <DefaultsSection view={view} draft={draft} set={set} routable={routable} />
      {/* RUN-EFFORT: the tier → model map (saves on its own, not from the dock). */}
      <EffortSection
        api={api}
        company={company}
        models={
          view.gateway.listed
            ? view.gateway.models.filter((m) => m.allowed).map((m) => m.alias)
            : []
        }
      />
      <ByoSection api={api} company={company} view={view} />
      <OverridesSection company={company} view={view} />
      <SaveDock
        dirty={dirty}
        saving={save.isPending}
        saveLabel="Save model policy"
        onSave={() => save.mutate()}
        onDiscard={() => {
          save.reset();
          setDraft(saved);
        }}
      />
    </SettingsLayout>
  );
}

type SectionProps = {
  view: ModelPolicyView;
  draft: Draft;
  set: (p: Partial<Draft>) => void;
  routable: string[];
};

function AllowedSection({ view, draft, set, routable }: SectionProps) {
  const residencyId = useId();
  const listId = useId();
  const regions = [...new Set(view.gateway.models.flatMap((m) => m.regions))].sort();
  const toggle = (alias: string, on: boolean) =>
    set({
      allowed: on
        ? [...draft.allowed.filter((a) => a !== alias), alias]
        : draft.allowed.filter((a) => a !== alias),
    });
  return (
    <SettingsSection
      id="allowed"
      icon={BoxesIcon}
      title="Allowed models"
      description="The gateway models your agents may use, and where they must be served."
    >
      <ToggleRow
        label="Every gateway model"
        description="Allow whatever the gateway serves now and later. Off: only the models ticked below."
        checked={draft.allowAll}
        onCheckedChange={(on) =>
          set({
            allowAll: on,
            allowed:
              on || draft.allowed.length ? draft.allowed : view.gateway.models.map((m) => m.alias),
          })
        }
      />
      <Field className="max-w-xs">
        <FieldLabel htmlFor={residencyId}>Residency</FieldLabel>
        <NativeSelect
          id={residencyId}
          value={draft.residency}
          onChange={(e) => set({ residency: e.target.value })}
          disabled={!view.gateway.listed}
        >
          <NativeSelectOption value="">Any region</NativeSelectOption>
          {regions.map((r) => (
            <NativeSelectOption key={r} value={r}>
              {r.toUpperCase()} only
            </NativeSelectOption>
          ))}
        </NativeSelect>
        <FieldDescription>
          A model the gateway names no region for never satisfies a residency.
        </FieldDescription>
      </Field>
      {view.gateway.listed ? (
        <Table data-slot="model-policy-models" aria-label="Gateway models">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <span className="sr-only">Allowed</span>
              </TableHead>
              <TableHead>Model</TableHead>
              <TableHead className="max-sm:hidden">Served in</TableHead>
              <TableHead className="text-right">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {view.gateway.models.map((m) => {
              const ticked = draft.allowAll || draft.allowed.includes(m.alias);
              const routes = routable.includes(m.alias);
              return (
                <TableRow key={m.alias}>
                  <TableCell>
                    <Checkbox
                      aria-label={`Allow ${m.alias}`}
                      checked={ticked}
                      disabled={draft.allowAll}
                      onCheckedChange={(on) => toggle(m.alias, on === true)}
                    />
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {m.alias}
                    {/* Phone width: the regions under the alias, not in their own column. */}
                    <span className="block font-sans text-muted-foreground sm:hidden">
                      {m.regions.map((r) => r.toUpperCase()).join(", ") || "No region"}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground max-sm:hidden">
                    {m.regions.length ? m.regions.map((r) => r.toUpperCase()).join(", ") : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    {routes ? (
                      <Badge variant="success" dot>
                        Allowed
                      </Badge>
                    ) : ticked ? (
                      <Badge variant="warning" dot>
                        Outside residency
                      </Badge>
                    ) : (
                      <Badge variant="outline">Not allowed</Badge>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : (
        <>
          <Notice tone="warning" data-slot="model-policy-unlisted">
            The gateway lists no models (LITELLM_MODELS), so the models below cannot be checked and
            no residency can be set. Name the aliases agents may use.
          </Notice>
          <Field>
            <FieldLabel htmlFor={listId}>Allowed model aliases</FieldLabel>
            <Input
              id={listId}
              value={draft.allowed.join(", ")}
              disabled={draft.allowAll}
              placeholder="claude-sonnet, gpt-4o"
              onChange={(e) =>
                set({
                  allowed: e.target.value
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
            />
            <FieldDescription>Comma-separated gateway model aliases.</FieldDescription>
          </Field>
        </>
      )}
    </SettingsSection>
  );
}

function ModelPicker({
  id,
  value,
  onChange,
  options,
  empty,
  listed,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  empty: string;
  listed: boolean;
}) {
  if (!listed && options.length === 0)
    return (
      <Input id={id} value={value} placeholder={empty} onChange={(e) => onChange(e.target.value)} />
    );
  // A saved value the draft no longer allows stays visible (and is refused on save).
  const all = value && !options.includes(value) ? [value, ...options] : options;
  return (
    <NativeSelect
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full"
    >
      <NativeSelectOption value="">{empty}</NativeSelectOption>
      {all.map((m) => (
        <NativeSelectOption key={m} value={m}>
          {options.includes(m) ? m : `${m} (not allowed)`}
        </NativeSelectOption>
      ))}
    </NativeSelect>
  );
}

function DefaultsSection({ view, draft, set, routable }: SectionProps) {
  const id = useId();
  const options = view.gateway.listed ? routable : draft.allowAll ? [] : draft.allowed;
  return (
    <SettingsSection
      id="defaults"
      icon={ListTreeIcon}
      title="Default per task kind"
      description="The model a run gets when neither the run nor its agent names one."
    >
      <div className="grid gap-4 sm:grid-cols-2" data-slot="model-policy-defaults">
        <Field className="sm:col-span-2 sm:max-w-sm">
          <FieldLabel htmlFor={`${id}-default`}>Company default</FieldLabel>
          <ModelPicker
            id={`${id}-default`}
            value={draft.defaultModel}
            onChange={(v) => set({ defaultModel: v })}
            options={options}
            empty="The gateway's first allowed model"
            listed={view.gateway.listed}
          />
        </Field>
        {view.task_kinds.map((k) => (
          <Field key={k}>
            <FieldLabel htmlFor={`${id}-${k}`}>{kindLabel(k)}</FieldLabel>
            <ModelPicker
              id={`${id}-${k}`}
              value={draft.kinds[k] ?? ""}
              onChange={(v) => set({ kinds: { ...draft.kinds, [k]: v } })}
              options={options}
              empty="Company default"
              listed={view.gateway.listed}
            />
          </Field>
        ))}
      </div>
    </SettingsSection>
  );
}

type FlagKey = keyof ModelPolicyView["byo"]["company"];

function ByoSection({
  api,
  company,
  view,
}: {
  api: Api;
  company: CompanyAccess;
  view: ModelPolicyView;
}) {
  const qc = useQueryClient();
  const setFlag = useMutation({
    mutationFn: async (x: { key: FlagKey; on: boolean }) => {
      // The PATCH replaces the whole settings object: read them fresh, so the model policy saved
      // on this page a moment ago (or by someone else) is kept.
      const d = await qc.fetchQuery({ ...companyQuery(api, company.id), staleTime: 0 });
      return api.client.companies.update({
        params: { co: company.id },
        body: {
          settings: {
            ...d.settings,
            flags: { ...(d.settings.flags ?? {}), [FLAG_OF[x.key]]: x.on },
          },
        },
      });
    },
    onSuccess: (d) => {
      qc.setQueryData(keys.company(company.id), d);
      void qc.invalidateQueries({ queryKey: modelPolicyKey(company.id) });
      void qc.invalidateQueries({ queryKey: subscriptionKeys.all(company.id) });
    },
    onError: (e) => toast.add({ title: problemText(e, "Not saved."), type: "error" }),
  });
  const { platform: p, company: flags, effective } = view.byo;
  const state = (on: boolean, platformOn: boolean) =>
    on ? (
      <Badge variant="success" dot>
        On
      </Badge>
    ) : platformOn ? (
      <Badge variant="outline">Off here</Badge>
    ) : (
      <Badge variant="neutral">Not opened on the platform</Badge>
    );
  return (
    <SettingsSection
      id="byo"
      icon={KeyRoundIcon}
      title="Own AI keys"
      description="Whether people may power their own agents and crew with their own provider keys."
    >
      <ToggleRow
        label="People may bring their own API keys"
        description={
          p.person_api_keys || p.company_api_keys
            ? "Personal provider API keys for their own agents; admins add company keys in Team › Connections › API keys."
            : "Personal provider API keys for their own agents. Not opened on the platform yet."
        }
        checked={flags.model_api_keys}
        disabled={!(p.person_api_keys || p.company_api_keys) || setFlag.isPending}
        onCheckedChange={(on) => setFlag.mutate({ key: "model_api_keys", on })}
      />
      <ToggleRow
        label="Own Claude Code logins"
        description={
          p.person_cli_subscriptions
            ? "People record their own logged-in Claude Code CLI for their own crew. No token is stored."
            : "People record their own logged-in Claude Code CLI for their own crew. Not opened on the platform yet."
        }
        checked={flags.model_cli_subscriptions}
        disabled={!p.person_cli_subscriptions || setFlag.isPending}
        onCheckedChange={(on) => setFlag.mutate({ key: "model_cli_subscriptions", on })}
      />
      <dl
        className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-2 border-t border-subtle pt-4 text-sm"
        data-slot="model-policy-byo-effective"
      >
        <dt>Personal API keys</dt>
        <dd className="justify-self-end">{state(effective.person_api_keys, p.person_api_keys)}</dd>
        <dt>Company API keys</dt>
        <dd className="justify-self-end">
          {state(effective.company_api_keys, p.company_api_keys)}
        </dd>
        <dt>Own CLI logins</dt>
        <dd className="justify-self-end">
          {state(effective.person_cli_subscriptions, p.person_cli_subscriptions)}
        </dd>
      </dl>
      <Notice tone="info" data-slot="model-policy-terms">
        <p>{COPY.keyNotice}</p>
        {p.person_cli_subscriptions ? null : <p className="mt-1.5">{COPY.cliOffWhy}</p>}
        <p className="mt-1.5">
          {COPY.pooledRefused}{" "}
          <a href={ANTHROPIC_TERMS_URL} target="_blank" rel="noreferrer" className="underline">
            Anthropic's terms
          </a>
          {" · "}
          {/* CONNECTIONS-AREA: the AI model keys live in Team › Connections › API keys. */}
          <Link
            to="/c/$slug/agents/connections"
            params={{ slug: company.slug }}
            search={{ tab: "keys" }}
            className="underline"
          >
            Team › Connections › API keys
          </Link>
        </p>
      </Notice>
    </SettingsSection>
  );
}

function OverridesSection({ company, view }: { company: CompanyAccess; view: ModelPolicyView }) {
  const rows = view.overrides.data;
  return (
    <SettingsSection
      id="overrides"
      icon={BotIcon}
      title="Per-agent overrides"
      description="Agents that name their own model. Read-only here; change each where it is set."
    >
      {rows.length === 0 ? (
        <EmptyState
          variant="card"
          icon={BotIcon}
          title="No agent overrides"
          description="Every agent uses the defaults above."
        />
      ) : (
        <Table data-slot="model-policy-overrides" aria-label="Per-agent model overrides">
          <TableHeader>
            <TableRow>
              <TableHead>Agent</TableHead>
              <TableHead className="max-sm:hidden">Set in</TableHead>
              <TableHead>Task kind</TableHead>
              <TableHead>Model</TableHead>
              <TableHead className="text-right">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((o) => (
              <OverrideRow
                key={`${o.membership_id}:${o.source}:${o.role ?? ""}`}
                o={o}
                slug={company.slug}
              />
            ))}
          </TableBody>
        </Table>
      )}
      {view.overrides.truncated ? (
        <p className="text-xs text-muted-foreground">Showing the first 200 overrides.</p>
      ) : null}
    </SettingsSection>
  );
}

function OverrideRow({ o, slug }: { o: PolicyOverride; slug: string }) {
  return (
    <TableRow>
      <TableCell className="font-medium">
        {o.agent_kind === "digital_employee" ? (
          <Link
            to="/c/$slug/agents/de/$id"
            params={{ slug, id: o.membership_id }}
            className="hover:underline"
          >
            {o.name}
          </Link>
        ) : (
          <Link to="/c/$slug/agents" params={{ slug }} className="hover:underline">
            {o.name}
          </Link>
        )}
      </TableCell>
      <TableCell className="text-muted-foreground max-sm:hidden">
        {o.source === "role_model_map" ? (
          <Link to="/c/$slug/setup/de-overrides" params={{ slug }} className="hover:underline">
            Standing orders & models
          </Link>
        ) : (
          "Agent profile"
        )}
      </TableCell>
      <TableCell>{o.role ? kindLabel(o.role) : "All"}</TableCell>
      <TableCell className="font-mono text-xs">{o.model}</TableCell>
      <TableCell className="text-right">
        {o.allowed === null ? (
          <Badge variant="outline">Unchecked</Badge>
        ) : o.allowed ? (
          <Badge variant="success" dot>
            Allowed
          </Badge>
        ) : (
          <Badge variant="danger" dot>
            Outside policy
          </Badge>
        )}
      </TableCell>
    </TableRow>
  );
}
